import type { TooltipFormatting } from '@shared/schemas/chartPreferences';
import { defaultChartPreferences } from '@shared/schemas/chartPreferences';

/**
 * Formats a numerical value for display in chart tooltips based on formatting preferences
 */
export function formatTooltipValue(
  value: number,
  preferences?: TooltipFormatting,
  currencySymbol?: string
): string {
  const prefs = preferences || defaultChartPreferences.tooltipFormatting;
  
  // Determine decimal places based on value
  let decimalPlaces: number;
  const absValue = Math.abs(value);
  
  if (absValue < 1) {
    decimalPlaces = prefs.decimalPlacesLessThanOne;
  } else if (absValue <= 30) {
    decimalPlaces = prefs.decimalPlacesOneToThirty;
  } else {
    decimalPlaces = prefs.decimalPlacesGreaterThanThirty;
  }
  
  // Format the number based on preferences
  let formattedValue: string;
  
  if (prefs.showLocalizedNumbers) {
    formattedValue = value.toLocaleString(undefined, {
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces,
      useGrouping: prefs.showThousandsSeparator,
    });
  } else {
    formattedValue = value.toFixed(decimalPlaces);
    
    // Add thousands separator if requested but not using localization
    if (prefs.showThousandsSeparator && Math.abs(value) >= 1000) {
      const parts = formattedValue.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      formattedValue = parts.join('.');
    }
  }
  
  // Add currency symbol if provided
  const symbolToUse = currencySymbol || prefs.currencySymbol;
  if (symbolToUse) {
    formattedValue = `${symbolToUse}${formattedValue}`;
  }
  
  return formattedValue;
}

/**
 * Formats a percentage value for display in chart tooltips
 */
export function formatTooltipPercentage(
  value: number,
  preferences?: TooltipFormatting
): string {
  const prefs = preferences || defaultChartPreferences.tooltipFormatting;
  
  const formattedValue = prefs.showLocalizedNumbers
    ? value.toLocaleString(undefined, {
        minimumFractionDigits: prefs.percentagePrecision,
        maximumFractionDigits: prefs.percentagePrecision,
        useGrouping: prefs.showThousandsSeparator,
      })
    : value.toFixed(prefs.percentagePrecision);
  
  return `${formattedValue}%`;
}

/**
 * Formats a label value (non-numeric) for display in chart tooltips
 */
export function formatTooltipLabel(
  label: any,
  preferences?: TooltipFormatting
): string {
  // For labels, we mainly just ensure proper string conversion
  // and handle any special date formatting if needed
  
  if (label instanceof Date) {
    return label.toLocaleDateString();
  }
  
  if (typeof label === 'string') {
    // Handle date-like strings
    if (/\d{4}-\d{2}-\d{2}/.test(label)) {
      const date = new Date(label);
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString();
      }
    }
    
    // Handle "Month YYYY" format
    if (/\w{3}\s+\d{4}/.test(label)) {
      return label; // Return as-is for "Month YYYY" format
    }
  }
  
  return String(label);
}

/**
 * Creates a custom tooltip formatter function for Recharts based on preferences
 */
export function createTooltipFormatter(
  preferences?: TooltipFormatting,
  currencySymbol?: string
) {
  return (value: any, name: string, props: any) => {
    if (typeof value === 'number') {
      // Check if this looks like a percentage (0-100 range and name contains %)
      if (name.includes('%') || (value >= 0 && value <= 100 && name.toLowerCase().includes('percent'))) {
        return [formatTooltipPercentage(value, preferences), name];
      }
      
      return [formatTooltipValue(value, preferences, currencySymbol), name];
    }
    
    return [String(value), name];
  };
}

/**
 * Creates a custom label formatter function for Recharts based on preferences
 */
export function createLabelFormatter(preferences?: TooltipFormatting) {
  return (label: any) => {
    return formatTooltipLabel(label, preferences);
  };
}

// --- New Semantic Type Formatting ---

export type YAxisType = 'number' | 'currency' | 'percent' | undefined;

/**
 * Formats a tooltip value based on its semantic type, ignoring user preferences.
 * This is used for charts generated by the AI where context is known.
 * @param value The raw numerical value.
 * @param yAxisType The semantic type of the y-axis.
 * @returns A formatted string.
 */
export function formatTooltipValueSemantically(value: number, yAxisType: YAxisType): string {
  if (typeof value !== 'number') {
    return String(value);
  }

  const options: Intl.NumberFormatOptions = {};
  const locale = 'en-US'; // Can be made dynamic later

  switch (yAxisType) {
    case 'currency':
      options.style = 'currency';
      options.currency = 'USD'; // Default, can be made dynamic
      options.minimumFractionDigits = 2;
      options.maximumFractionDigits = 2;
      break;
    
    case 'percent':
      options.style = 'percent';
      options.minimumFractionDigits = 1;
      options.maximumFractionDigits = 1;
      break;

    case 'number':
    default:
      if (Math.abs(value) < 1) {
        options.minimumFractionDigits = 2;
      } else if (Math.abs(value) < 30) {
        options.minimumFractionDigits = 1;
      } else {
        options.minimumFractionDigits = 0;
      }
      options.maximumFractionDigits = options.minimumFractionDigits;
      break;
  }

  try {
    return new Intl.NumberFormat(locale, options).format(value);
  } catch (e) {
    console.error("Error formatting number semantically:", e);
    return String(value);
  }
} 